/* screens/settings/SubscriptionTab.jsx — личный тариф (window.SubscriptionTab)
   + общий window.BillingView (scope personal|business) — единый биллинг-вид для
   личного тарифа и BusinessTariffPanel (унификация PlanCard/PaymentsList/UsageBar, фикс №15).
   Текущий тариф + usage + pending · доступные тарифы (апгрейд→checkout, даунгрейд→pending) · история.
   ХЕНДОФФ → frontend/pages/settings/SubscriptionTab.jsx + components/common/BillingView.jsx. */

import React from 'react';
import { useAuth } from '../../app/providers/AuthProvider.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { InvoicePayModal } from '../../components/patterns/InvoicePayModal.jsx';
import { PaymentMethodModal } from '../../components/patterns/PaymentMethodModal.jsx';
import { PaymentsList } from '../../components/patterns/PaymentsList.jsx';
import { PlanCard, currentPriceOf, queuedStartsAt } from '../../components/patterns/PlanCard.jsx';
import { PlanPeriodModal } from '../../components/patterns/PlanPeriodModal.jsx';
import { RecurringConsentModal } from '../../components/patterns/RecurringConsentModal.jsx';
import { QueryBoundary } from '../../components/patterns/States.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { hooks } from '../../hooks/index.jsx';
import { subscriptionReal } from '../../lib/adapters/subscription.js';
import { consentNeededForPlan, consentNeededForPlanChange, isConsentRequiredError, isPriceChangedError } from '../../lib/recurringConsent.js';
import { priceChangedNotice } from '../../lib/recurringConsentText.js';
import { RR } from '../../lib/router.jsx';
import { usePhoneGate } from '../../components/patterns/PhoneGate.jsx';
import { crosslinkVariant } from './billingCrosslink.js';
import { nextOverride, withOverride } from './subscriptionOverride.js';

  const { useState } = React;
  const { useFakeQuery } = hooks;
  const { useNavigate } = RR;

  function Section({ title, hint, children }) {
    return (
      <section className="bill-section">
        <div className="bill-section__head"><h3 className="t-h4">{title}</h3>{hint && <span className="t-caption text-mute">{hint}</span>}</div>
        {children}
      </section>
    );
  }

  // Единый биллинг-вид (личный/бизнес). canManage=false (не админ/владелец бизнес-ws) →
  // статус тарифа виден, но управление (апгрейд/даунгрейд/период/отмена pending) скрыто:
  // бэк всё равно отдаёт 403 не-админу на checkout/change/cancel, не даём наткнуться на сюрприз.
  /* Развилка перед оплатой личного тарифа. Не блокировка: «Всё верно» стоит
     ПЕРВОЙ и работает в один клик — покупать личного ассистента при живом
     ресторане законно, и мешать этому нельзя. Задача экрана — назвать вещи
     своими именами до оплаты, а не после неё в поддержке.
     Отказ уходит на сервер: второй раз не переспрашиваем. */
  function PersonalPlanWarningModal({ open, plan, onClose, onProceed, onGoBusiness }) {
    const [saving, setSaving] = useState(false);
    if (!open || !plan) return null;
    const signals = (plan.warn_personal && plan.warn_personal.signals) || [];
    const place = signals.find((x) => x.kind === 'workspace');
    const go = async () => {
      setSaving(true);
      // Отметку ставим ДО оплаты и не роняем поток, если она не записалась:
      // человек уже принял решение, и мешать ему из-за сбоя отметки нельзя.
      try { await subscriptionReal.dismissPersonalWarning(); } catch { /* noop */ }
      setSaving(false);
      onProceed();
    };
    return (
      <Modal open={open} onClose={onClose} title="Это тариф личного ассистента" size="sm"
        className="personal-warn">
        <div className="stack stack-12">
          <p className="t-body">
            В «{plan.name}» входят ассистент, техкарты и калькуляция.
          </p>
          <ul className="plan-card__features plan-card__features--missing">
            <li><Icon name="close" size={14} /><span>Аналитика кассы: выручка, ОПиУ, ABC</span></li>
            <li><Icon name="close" size={14} /><span>Командный доступ и общие данные</span></li>
          </ul>
          <p className="t-body text-dim">
            {place
              ? `У вас есть ресторан «${place.label}» — аналитика по нему оплачивается тарифом бизнес-пространства.`
              : 'Аналитика по ресторану оплачивается тарифом бизнес-пространства.'}
          </p>
          <div className="cluster cluster-8">
            <Button variant="primary" loading={saving} onClick={go}>Всё верно, беру ассистента</Button>
            <Button variant="ghost" onClick={onGoBusiness}>Показать тарифы ресторана</Button>
          </div>
        </div>
      </Modal>
    );
  }

  function BillingView({ scope = 'personal', searchParams, wsId, canManage = true }) {
    const toast = useToast();
    const navigate = useNavigate();
    const { user, workspaces } = useAuth();
    // Подсказка-перекрёстка между вкладками без обещания «Аналитики» там, где тариф её
    // закрыл (workspace.sections.analytics === false) — см. billingCrosslink.js.
    const crosslink = crosslinkVariant(scope, {
      workspace: (workspaces || []).find((w) => w.id === wsId) || null,
      businessWorkspaces: (workspaces || []).filter((w) => w.kind === 'business'),
    });
    const sp = searchParams;
    // wsId в ключе → подписка/история перезапрашиваются при смене целевого
    // пространства (личный закреплён за personal-ws; бизнес — за выбранным ws-id),
    // иначе useFakeQuery отдавал бы кэш прошлого пространства (актуальные данные).
    const key = scope + ':' + (wsId != null ? wsId : 'active') + ':' + (sp ? sp.toString() : '');
    // wsId: personal → id личного ws (закреплён в SubscriptionTab); business → явный id (проп).
    const subQ = useFakeQuery('sub:' + key, () => subscriptionReal.getSubscription(wsId), [key]);
    const plansQ = useFakeQuery('plans:' + scope, () => subscriptionReal.listPlans(), [scope]);
    const payQ = useFakeQuery('pay:' + key, () => subscriptionReal.listPayments(wsId), [key]);

    // Локальные правки подписки после действий на экране — только для этого ключа
    // (subscriptionOverride.js): панель не перемонтируется при смене заведения.
    const [override, setOverride] = useState(null);
    const [busy, setBusy] = useState(false);
    const [periodFamily, setPeriodFamily] = useState(null);   // открытое модальное «выбор периода»
    const [payMethodSlug, setPayMethodSlug] = useState(null); // развилка способа оплаты (карта/счёт)
    const [invoiceSlug, setInvoiceSlug] = useState(null);     // модалка реквизитов счёта (Точка)
    const [consentSlug, setConsentSlug] = useState(null);     // согласие на автосписания перед оплатой картой
    const [changeSlug, setChangeSlug] = useState(null);       // согласие на списания по цене тарифа дешевле
    // Сервер ответил 409 price_changed: цена тарифа сменилась, пока окно согласия было
    // открыто. {slug, was, now} — окно показывается заново с новой ценой и пояснением.
    const [changeRepriced, setChangeRepriced] = useState(null);

    const sub = withOverride(subQ.data, override, key);
    const plans = plansQ.data || [];
    // Текущий тариф — из витрины, а если его там нет, из самой подписки (/me/).
    // Витрина ws-scoped: закрытый (сетевой) тариф сервер кладёт в неё только
    // тому пространству, что сейчас активно, а эта панель открывается и на
    // другом заведении (селектор в «Заведениях» активное ws не переключает).
    // Без фолбэка блок «Текущий тариф» у сетевого клиента просто исчезал, а
    // currentPrice падал в 0 — и весь грид выглядел апгрейдом с оплатой.
    const current = sub ? (plans.find((p) => p.slug === sub.plan) || sub.plan_details || null) : null;
    const currentPrice = currentPriceOf(current, sub);
    const pendingName = sub && sub.pending_plan ? (plans.find((p) => p.slug === sub.pending_plan) || {}).name : null;
    // Линейки разнесены по аудитории: командные тарифы (team_members) — только в
    // разделе «Для бизнеса», личные — в «Тариф». Текущий план резолвим из полного
    // списка (чтобы карточка текущего показалась в любом scope), а альтернативы —
    // фильтруем по scope.
    // Аудиторию тариф несёт САМ (поле audience), и она уже нормализована
    // адаптером (`adaptPlan` → `audienceOf`, там же фолбэк на старый признак
    // team_members). Повторять правило здесь нельзя: ровно так оно и разъехалось
    // между витриной и визардом в прошлый раз.
    const inScope = (p) => p.audience === scope;

    // Семейства тарифов с периодами: slug вида <base>_<N>m — длинный вариант base.
    // Длинные варианты не показываем отдельными карточками — они уходят в модалку
    // «выбор периода», которая открывается с карточки базового (месячного) тарифа.
    const isVariant = (p) => /_\d+m$/.test(p.slug);
    const variantsByBase = {};
    plans.forEach((p) => {
      const m = /^(.+)_(\d+)m$/.exec(p.slug);
      if (m) (variantsByBase[m[1]] = variantsByBase[m[1]] || []).push({ plan: p, months: Number(m[2]) });
    });
    const familyOf = (p) => (p && variantsByBase[p.slug] && variantsByBase[p.slug].length
      ? { base: p, variants: variantsByBase[p.slug] } : null);
    // Текущий план в многопериодном семействе → даём сменить период из карточки текущего.
    // Слаг берём из ПОДПИСКИ, а не из найденной карточки витрины: тариф может в
    // витрину не попасть (см. выше), а отсечение ниже обязано работать всё равно.
    const currentSlug = sub ? sub.plan : null;
    const currentBaseSlug = currentSlug ? currentSlug.replace(/_\d+m$/, '') : null;
    const currentBase = currentBaseSlug ? plans.find((p) => p.slug === currentBaseSlug) : null;
    const currentFamily = familyOf(currentBase);

    // Из грида прячем и текущий план, и базу его семейства (когда юзер сидит на
    // 6/12-мес варианте) — иначе Expert дублировался бы карточкой + кнопкой «Сменить период».
    // Отсечение БЕЗУСЛОВНОЕ: раньше условие начиналось с `!current ||`, и стоило
    // тарифу не найтись в витрине — грид переставал прятать что-либо вообще.
    // Для закрытого тарифа это и есть та самая дыра: сервер кладёт его в список
    // именно как «тариф этого клиента», а грид предлагал бы купить уже купленное.
    // Демо-тарифы из выбора прячем — на них не переключаются сменой тарифа:
    // дефолтный бесплатный `demo` пространство получает само (новое ws / после
    // отмены продления, см. кнопку «Отменить продление подписки»), а «Быстрый
    // старт» `biz_demo` включается через онбординг/trial. Бэк тоже отбивает
    // /change/ на оба как plan_not_switchable.
    // Закрытые тарифы отсекаем ПО ПРИЗНАКУ, а не по совпадению слага: перейти на
    // них самообслуживанием нельзя в принципе — их продаёт менеджер именной
    // ссылкой, и бэк отобьёт такой checkout как unknown_plan.
    // Оплаченная акция в очереди — уже куплена: второй раз её не предлагаем
    // (она видна на карточке текущего тарифа как «Оплачен тариф …»).
    const prepaidSlug = sub && sub.pending_plan_prepaid ? sub.pending_plan : null;
    const alternatives = plans.filter((p) =>
      p.slug !== currentSlug && p.slug !== currentBaseSlug && !p.is_private
      && inScope(p) && !isVariant(p) && p.slug !== 'biz_demo' && p.slug !== 'demo'
      && p.slug !== prepaidSlug);

    // Безналичная оплата по счёту (Точка) — личный И бизнес-скоуп (этап 3), админу
    // и при включённом флаге. Если доступна — апгрейд идёт через развилку способа оплаты.
    const showInvoicePay = canManage && !!(user && user.tochka_invoicing_enabled);
    // Оплата из-за рубежа (иностранная карта, канал инокарт Робокассы) — админу и
    // при включённом флаге. Нерекуррентно: карта не привязывается, автопродления нет.
    const showForeignPay = canManage && !!(user && user.robokassa_foreign_enabled);
    const showPayMethodChoice = showInvoicePay || showForeignPay;

    // Выбор периода в модалке: то же решение up/down, что и карточка тарифа.
    const onSelectPeriod = (slug, price) => {
      setPeriodFamily(null);
      if (!sub || slug === sub.plan || slug === sub.pending_plan) return;
      if (price > currentPrice) onUpgrade(slug);
      else onDowngrade(slug);
    };

    // Карточная оплата (Робокасса) — прежнее поведение, вынесено в отдельную функцию,
    // чтобы развилка способа оплаты могла её вызвать напрямую.
    // method='foreign_card' — та же Робокасса, но нерекуррентный платёж инокартой.
    // consent=true — человек поставил галочку согласия на автосписания (см. payByCard).
    const startCardCheckout = async (slug, method, consent) => {
      setBusy(true);
      try {
        const r = await subscriptionReal.startCheckout(slug, wsId, method, consent);
        // Мок-шлюз (DEBUG) — это экран самого прототипа: уходим во ВНУТРЕННИЙ hash-роут по invoice_id,
        // а не на абсолютный redirect_url (он ведёт на FRONTEND_URL боевого фронта). Внешний шлюз
        // (Робокасса) — реальный редирект.
        if (r && r.invoice_id && r.redirect_url && /\/checkout\/mock\//.test(r.redirect_url)) {
          setConsentSlug(null);
          // бизнес-scope: счёт на не-активном ws → прокидываем ws в checkout, чтобы getPaymentStatus нашёл платёж.
          navigate('/checkout/mock/' + encodeURIComponent(r.invoice_id) + (wsId ? '?ws=' + encodeURIComponent(wsId) : '')); return;
        }
        if (r && r.redirect_url) { window.location.href = r.redirect_url; return; }
        toast.show('Не удалось начать оплату', 'danger');
      } catch (e) {
        // Сервер требует согласие, а экран его не спросил (правила разошлись или
        // тариф не нашёлся в витрине): не тупик с тостом, а то же окно с галочкой.
        if (isConsentRequiredError(e) && !consent && method !== 'foreign_card') { setConsentSlug(slug); return; }
        toast.show((e && e.detail) || 'Не удалось начать оплату', 'danger');
      }
      finally { setBusy(false); }
    };
    // Оплата российской картой привязывает карту к автопродлению — перед уходом в
    // Робокассу показываем условия и обязательную галочку. Разовому тарифу
    // (renewable=false) согласие не нужно: списаний по нему не будет.
    const payByCard = (slug) => {
      if (consentNeededForPlan(plans.find((p) => p.slug === slug))) setConsentSlug(slug);
      else startCardCheckout(slug);
    };
    // Апгрейд: если доступен хотя бы один способ кроме обычной карты (безнал Точки
    // и/или зарубежная карта) — сперва показываем выбор способа оплаты.
    // Иначе — сразу картой, как раньше.
    // Развилка перед оплатой личного тарифа. Показывается, только если СЕРВЕР
    // сказал показать (plan.warn_personal): правило живёт в subscriptions/audience.py
    // и здесь не повторяется — иначе условия разошлись бы молча.
    const [warnPlan, setWarnPlan] = useState(null);
    const phoneGate = usePhoneGate();
    // Телефон спрашиваем ОДИН раз перед первой оплатой — у кого он уже есть,
    // шага не будет. Гейт стоит на общей точке proceed, а не на каждой кнопке:
    // сюда сходятся и грид тарифов, и модалка выбора периода, и путь после
    // предупреждения о личном тарифе.
    const proceed = (slug) => phoneGate.run(() => {
      if (showPayMethodChoice) setPayMethodSlug(slug);
      else payByCard(slug);
    });
    const onUpgrade = (slug) => {
      const p = plans.find((x) => x.slug === slug);
      if (p && p.warn_personal) { setWarnPlan(p); return; }
      proceed(slug);
    };
    // Понижение: тариф дешевле включится оплатой продления по ЕГО цене. Если следующий
    // период спишут с карты, сначала — согласие на новую сумму (S33, то же окно, что
    // перед оплатой картой, в режиме «смена тарифа»). Счёт клиент оплачивает сам.
    const onDowngrade = (slug) => {
      if (consentNeededForPlanChange(sub)) { setChangeSlug(slug); return; }
      requestDowngrade(slug);
    };
    // expectedPrice — цена, показанная в окне согласия: сервер сверяет её с ценой на
    // момент клика и при расхождении ничего не пишет (409 price_changed, S33).
    const requestDowngrade = async (slug, consent, expectedPrice) => {
      setBusy(true);
      try {
        const updated = await subscriptionReal.changePlan(slug, wsId, consent, expectedPrice);
        // Весь свежий ответ: карточке нужны цена нового тарифа и канал оплаты, а не
        // только slug перехода.
        setOverride((o) => nextOverride(o, key, updated));
        setChangeSlug(null);
        setChangeRepriced(null);
        toast.show('Переход на «' + (plans.find((p) => p.slug === slug) || {}).name + '» запланирован с конца периода', 'success');
      } catch (e) {
        // Сервер требует согласие, а экран его не спросил (старый ответ без канала):
        // не тупик с тостом, а то же окно с галочкой.
        if (isConsentRequiredError(e) && !consent) { setChangeSlug(slug); return; }
        // Цена сменилась, пока окно было открыто: ничего не записано. Перечитываем
        // тарифы и показываем то же окно заново — с новой ценой, пояснением «было →
        // стало» и снятой галочкой (её ставят уже под новой ценой).
        if (isPriceChangedError(e)) {
          setChangeRepriced({ slug, was: e.expected_price_rub ?? expectedPrice ?? null, now: e.price_rub ?? null });
          setChangeSlug(slug);
          plansQ.reload();
          return;
        }
        toast.show((e && e.detail) || 'Не удалось сменить тариф', 'danger');
      }
      finally { setBusy(false); }
    };
    const closeChange = () => { setChangeSlug(null); setChangeRepriced(null); };
    const onCancelPending = async () => {
      setBusy(true);
      try {
        const updated = await subscriptionReal.cancelPendingChange(wsId);
        setOverride((o) => nextOverride(o, key, updated));
        toast.show('Запланированный переход отменён', 'muted');
      }
      catch (e) { toast.show((e && e.detail) || 'Не удалось отменить', 'danger'); }
      finally { setBusy(false); }
    };
    // Автопродление: «Отменить продление» (тариф доработает до конца срока) /
    // «Возобновить». Только для платного активного плана (см. PlanCard).
    const onSetAutoRenew = async (enabled) => {
      setBusy(true);
      try {
        const updated = await subscriptionReal.setAutoRenew(enabled, wsId);
        setOverride((o) => nextOverride(o, key, { auto_renew: updated.auto_renew }));
        // Ждущий счёт на продление остаётся действительным — сервер говорит, что будет
        // при его оплате (`notice`); такой тост держим дольше, его надо успеть прочесть.
        const off = 'Продление отменено — тариф действует до конца оплаченного срока';
        toast.show(enabled
          ? 'Автопродление включено'
          : (updated.notice ? off + '. ' + updated.notice : off),
          enabled ? 'success' : 'muted',
          !enabled && updated.notice ? 10000 : undefined);
      } catch (e) { toast.show((e && e.detail) || 'Не удалось изменить автопродление', 'danger'); }
      finally { setBusy(false); }
    };

    // подписка и тарифы загружаются параллельно — оба нужны для каркаса;
    // сбой любого → единая ветка ошибки с перезагрузкой обоих (фикс P2-23).
    // Перечитывание УЖЕ загруженной витрины (409 price_changed) каркас не прячет: иначе
    // скелетон снял бы открытое окно согласия посреди шага. Витрина одна на scope, чужих
    // данных в ней не бывает — в отличие от подписки, которая меняется со сменой заведения.
    const headLoading = subQ.loading || (plansQ.loading && !plansQ.data);
    const headError = subQ.error || plansQ.error;
    const reloadHead = () => { subQ.reload(); plansQ.reload(); };
    const consentPlan = consentSlug ? (plans.find((p) => p.slug === consentSlug) || null) : null;
    const changeTarget = changeSlug ? (plans.find((p) => p.slug === changeSlug) || null) : null;
    // Цена в окне согласия: после 409 price_changed — актуальная из ответа сервера (витрина
    // ещё может перечитываться), иначе — из витрины. Её же шлём как expected_price_rub.
    const repriced = changeRepriced && changeRepriced.slug === changeSlug ? changeRepriced : null;
    const changePrice = repriced && repriced.now != null
      ? repriced.now : (changeTarget ? changeTarget.price_rub : null);

    return (
      <QueryBoundary loading={headLoading} error={headError} onRetry={reloadHead}
        skeleton={<div className="stack stack-16"><span className="skeleton" style={{ height: 260, borderRadius: 'var(--radius-lg)' }} /><span className="skeleton" style={{ height: 180, borderRadius: 'var(--radius-lg)' }} /></div>}>
        {sub && (
          <div className="billing stack stack-24">
            {!canManage && (
              <div className="callout callout--info">
                <Icon name="lock" size={16} />
                <div className="grow">
                  <div className="t-caption text-dim">Менять тариф может только администратор пространства.</div>
                </div>
              </div>
            )}

            {current && (
              <Section title="Текущий тариф">
                {/* не-админ: тариф виден, но отмену pending-перехода прячем (бэк отдаст 403) —
                    срезаем pending_plan из карточки, чтобы не показывать неактивную кнопку «Отменить». */}
                <PlanCard scope={scope} plan={current} subscription={canManage ? sub : { ...sub, pending_plan: null }} isCurrent
                  currentPrice={currentPrice} pendingPlanName={pendingName}
                  onCancelPending={canManage ? onCancelPending : undefined}
                  onSetAutoRenew={canManage ? onSetAutoRenew : undefined} busy={busy} />
                {/* Пока в очереди оплаченная акция, сменить период нельзя (бэк отбивает
                    prepaid_pending) — кнопку не показываем, чтобы не вести в отказ. */}
                {canManage && currentFamily && !(sub && sub.pending_plan_prepaid) && (
                  <div className="bill-section__aside">
                    <Button variant="ghost" size="sm" iconLeft="calendar" disabled={busy}
                      onClick={() => setPeriodFamily(currentFamily)}>Сменить период оплаты</Button>
                  </div>
                )}
              </Section>
            )}

            {/* Перевод на нужную вкладку. Ключевая правка: ресторан приходил сюда,
                видел единственный список с ценами и покупал ассистента, ожидая
                аналитику. Теперь на личной вкладке прямо сказано, где тариф
                ресторана, а на бизнес-вкладке — где личный. */}
            {canManage && (
              <div className="bill-crosslink">
                <Icon name="info" size={14} />
                {crosslink === 'personal-analytics' ? (
                  <span>
                    Это личный тариф: аналитики кассы и команды в нём нет.
                    Нужна аналитика по ресторану — она во вкладке{' '}
                    <a href="/settings?tab=business&biz=tariff">«Заведения»</a>.
                  </span>
                ) : crosslink === 'personal-plain' ? (
                  <span>
                    Это личный тариф: команды в нём нет. Тариф заведения — во вкладке{' '}
                    <a href="/settings?tab=business&biz=tariff">«Заведения»</a>.
                  </span>
                ) : crosslink === 'business-plain' ? (
                  <span>
                    Это тариф заведения: ассистент и команда.
                    Нужен только личный ассистент — он во вкладке{' '}
                    <a href="/settings?tab=subscription">«Личный тариф»</a>.
                  </span>
                ) : (
                  <span>
                    Это тариф заведения: ассистент, аналитика кассы и команда.
                    Нужен только личный ассистент — он во вкладке{' '}
                    <a href="/settings?tab=subscription">«Личный тариф»</a>.
                  </span>
                )}
              </div>
            )}

            {canManage && alternatives.length > 0 && (
              <Section
                title={scope === 'personal' ? 'Личные тарифы' : 'Тарифы заведения'}
                hint="Апгрейд — сразу с пропорциональной доплатой; даунгрейд — с конца периода">
                <div className="plan-grid">
                  {alternatives.map((p) => {
                    // Базовый тариф многопериодного семейства (напр. Expert) → клик
                    // открывает модалку с периодами и скидками, а не сразу checkout.
                    const fam = familyOf(p);
                    const openPeriods = fam ? () => setPeriodFamily(fam) : null;
                    return (
                      <PlanCard scope={scope} key={p.slug} plan={p} subscription={sub} currentPrice={currentPrice}
                        actionLabel={fam ? 'Выбрать период оплаты' : undefined}
                        onUpgrade={openPeriods || onUpgrade} onDowngrade={openPeriods || onDowngrade} busy={busy} />
                    );
                  })}
                </div>
              </Section>
            )}

            {/* Вход для ПОЛУЧАТЕЛЯ подарка. Стоит именно здесь, а не в меню
                профиля: человек с сертификатом на руках идёт туда, где
                тарифы, — он пришёл включить тариф, а не изучать аккаунт.
                Вторая половина пары (покупка) живёт в меню профиля. */}
            {canManage && (
              <div className="bill-crosslink">
                <Icon name="gift" size={14} />
                <span>
                  Вам подарили Ресториум?{' '}
                  <a href="/gift/redeem">Активируйте подарочный сертификат</a> —
                  тариф включится сразу.
                </span>
              </div>
            )}

            <Section title="История платежей">
              <PaymentsList payments={payQ.data || []} loading={payQ.loading}
                error={payQ.error} onRetry={payQ.reload} />
            </Section>
          </div>
        )}
        <PersonalPlanWarningModal
          open={!!warnPlan} plan={warnPlan}
          onClose={() => setWarnPlan(null)}
          onProceed={() => { const p = warnPlan; setWarnPlan(null); proceed(p.slug); }}
          onGoBusiness={() => { setWarnPlan(null); navigate('/settings?tab=business&biz=tariff'); }} />
        {phoneGate.modal}
        <PlanPeriodModal open={!!periodFamily} family={periodFamily} currentSlug={sub ? sub.plan : null}
          pendingSlug={sub ? sub.pending_plan : null}
          busy={busy} onSelect={onSelectPeriod} onClose={() => setPeriodFamily(null)} />
        {showPayMethodChoice && (
          <PaymentMethodModal
            open={!!payMethodSlug}
            plan={payMethodSlug ? (plans.find((p) => p.slug === payMethodSlug) || null) : null}
            startsAt={payMethodSlug
              ? queuedStartsAt(plans.find((p) => p.slug === payMethodSlug), sub, currentPrice) : null}
            busy={busy}
            showInvoice={showInvoicePay}
            showForeign={showForeignPay}
            onClose={() => setPayMethodSlug(null)}
            onPickCard={() => { const s = payMethodSlug; setPayMethodSlug(null); payByCard(s); }}
            onPickInvoice={() => { setInvoiceSlug(payMethodSlug); setPayMethodSlug(null); }}
            onPickForeign={() => { const s = payMethodSlug; setPayMethodSlug(null); startCardCheckout(s, 'foreign_card'); }} />
        )}
        {/* Согласие на автосписания — последний шаг перед Робокассой. Вне условия
            showPayMethodChoice: оно нужно и там, где развилки способов нет. */}
        <RecurringConsentModal
          open={!!consentSlug}
          planName={consentPlan ? consentPlan.name : ''}
          priceRub={consentPlan ? consentPlan.price_rub : null}
          periodDays={consentPlan ? consentPlan.period_days : null}
          // Апгрейд с действующего платного тарифа: первый платёж — доплата за
          // остаток периода, её считает сервер (calculate_proration). Акция
          // (`starts_after_current`) апгрейдом не бывает: полная цена, при идущем
          // тарифе — в очередь за ним (решение владельца 19.10.2026).
          prorated={!!(sub && sub.is_active && currentPrice > 0)
            && !(consentPlan && consentPlan.starts_after_current)}
          busy={busy}
          onClose={() => setConsentSlug(null)}
          onConfirm={() => startCardCheckout(consentSlug, undefined, true)} />
        {/* Понижение с картой: первое списание по цене нового тарифа — в день
            окончания текущего, с уже привязанной карты. */}
        <RecurringConsentModal
          open={!!changeSlug}
          planName={changeTarget ? changeTarget.name : ''}
          priceRub={changePrice}
          periodDays={changeTarget ? changeTarget.period_days : null}
          startsOn={(sub && (sub.expires_at || (sub.period && sub.period.end))) || null}
          notice={repriced ? priceChangedNotice(repriced.was, repriced.now) : null}
          busy={busy}
          onClose={closeChange}
          onConfirm={() => requestDowngrade(changeSlug, true, changePrice)} />
        {showInvoicePay && (
          <InvoicePayModal open={!!invoiceSlug} fixedPlanSlug={invoiceSlug}
            plans={plans} wsId={wsId} onClose={() => setInvoiceSlug(null)}
            onCreated={(invoiceId) => {
              setInvoiceSlug(null);
              navigate('/checkout/invoice/' + encodeURIComponent(invoiceId) + (wsId ? '?ws=' + encodeURIComponent(wsId) : ''));
            }} />
        )}
      </QueryBoundary>
    );
  }

  function SubscriptionTab({ searchParams }) {
    // Вкладка «Тариф» — ВСЕГДА личный тариф (personal-workspace), независимо от
    // активного пространства: бизнес-тариф живёт в разделе «Для бизнеса».
    // Передаём явный id личного ws (а не undefined → активный X-Workspace-Id),
    // чтобы из бизнес-пространства тут не показывался бизнес-тариф.
    const { workspaces } = useAuth();
    const personal = (workspaces || []).find((w) => w.kind === 'personal' || w.is_personal);
    return <div className="settings-panel"><BillingView scope="personal" searchParams={searchParams} wsId={personal ? personal.id : undefined} /></div>;
  }

export { BillingView };
export { SubscriptionTab };
